<template>
  <div>
    <PageAllow controller="MaterialSales" action="materialDailySaleCount" />
    <Breadcrumbs :items="items" />

    <v-sheet class="overflow-y-scroll fill-content-height">
      <v-row dense class="mx-5">
        <v-col cols="12" sm="12" md="4">
          <v-row dense align="center" class="pa-1">
            <SearcherDatetimeRange
              type="datetime"
              v-model="datetimeRage"
              :label="$t('MaterialDailySale.Fields.distribution_time')"
              @changedModel="datetimeRageChanged = $event"
              removeIcon
            />
          </v-row>
        </v-col>
        <v-col cols="12" sm="12" md="8" align-self="center">
          <v-row dense align="center" class="search-controls">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  small
                  text
                  class="px-0 py-0 caption font-weight-bold grey--text"
                  color="primary"
                  @click.stop="onSearch()"
                >
                  <v-icon small class="pe-1">fa-search</v-icon>
                  {{ $t("Search.search") }}
                </v-btn>
              </template>
              <span>{{ $t("Search.searchTip") }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  small
                  text
                  class="caption font-weight-bold grey--text"
                  @click.stop="resetSearch()"
                >
                  <v-icon x-small class="pe-1">fa-trash-alt</v-icon>
                  {{ $t("Search.reset") }}
                </v-btn>
              </template>
              <span>{{ $t("Search.resetTip") }}</span>
            </v-tooltip>
            <v-divider vertical />
            <v-btn
              class="caption font-weight-bold grey--text"
              :class="
                $vuetify.theme.dark ? 'text--lighten-1' : 'text--darken-1'
              "
              small
              text
              @click.stop="$store.commit('toggleSearchPanel')"
            >
              {{ $t("Search.pro") }}
              <v-icon small class="ps-1">{{
                $store.state.showSearchPanel ? "fa-angle-up" : "fa-angle-down"
              }}</v-icon>
            </v-btn>
            <v-spacer />
            <v-btn
              v-if="materialSelected && $permission.permissionAllow('MaterialSales', 'materialDailySaleCountExport')"
              small
              color="print"
              dark
              class="ms-1"
              @click.stop="exportExcel()"
            >
              <v-icon x-small class="pe-1">fa-file-excel</v-icon>
              {{ $t("Export.excel") }}
            </v-btn>
          </v-row>
        </v-col>
      </v-row>
      <v-expansion-panels
        v-model="$store.getters.panels"
        multiple
        flat
        accordion
      >
        <v-expansion-panel class="transparent-background-color">
          <v-expansion-panel-content>
            <v-row dense align="center">
              <!-- 商品 -->
              <v-col cols="12" sm="6" md="4">
                <SearcherMaterial v-model="materialSelected" />
              </v-col>
              <!-- 门店 -->
              <v-col cols="12" sm="6" md="4">
                <SearcherShops
                  v-model="shopsSelected"
                  :allianceBusinessIds="options.alliance_business_ids"
                />
              </v-col>
              <!-- 仓库 -->
              <v-col cols="12" sm="6" md="4" v-if="materialSelected">
                <template>
                  <v-autocomplete
                    color="primary"
                    hide-no-data
                    v-model="stocksSelected"
                    :items="stocksItems"
                    :loading="stockLoading"
                    :search-input.sync="stockSearch"
                    chips
                    multiple
                    clearable
                    deletable-chips
                    small-chips
                    item-text="Description"
                    item-value="id"
                    item-disabled="disabled"
                    :label="$t('Stock.ObjectName')"
                    return-object
                  ></v-autocomplete>
                </template>
              </v-col>
            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <div class="mx-4 mb-4">
        <v-data-table
          :headers="headers"
          :items="dailySaleItems"
          :options.sync="options"
          :page.sync="paging.page"
          :server-items-length="paging.count"
          :loading="loading"
          multi-sort
          class="elevation-1 px-4 pt-1"
          :footer-props="$t('$config.footer-props')"
          hide-default-header
        >
          <template v-slot:header>
            <thead class="v-data-table-header">
              <tr>
                <th
                  rowspan="2"
                  class="text-start text-no-wrap"
                >
                  {{ $t("MaterialDailySale.Fields.date") }}
                </th>
                <th
                  v-for="stock in stockColumns"
                  :key="'hdr_' + stock.stock_id"
                  colspan="3"
                  class="text-center text-no-wrap"
                >
                  {{ stock.stock_name }}
                  <v-tooltip bottom :disabled="!stock.shops || !stock.shops.length">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        v-if="stock.shops && stock.shops.length"
                        x-small
                        color="primary"
                        class="ml-1"
                        v-bind="attrs"
                        v-on="on"
                      >fa-info-circle</v-icon>
                    </template>
                    <span v-for="(shop, idx) in stock.shops" :key="idx">
                      {{ shop }}<br v-if="idx < stock.shops.length - 1" />
                    </span>
                  </v-tooltip>
                </th>
              </tr>
              <tr>
                <th
                  v-for="sh in subHeaders"
                  :key="sh.key"
                  class="text-center text-no-wrap"
                >
                  {{ sh.text }}
                </th>
              </tr>
            </thead>
          </template>
          <template v-slot:body.prepend>
            <tr>
              <td class="text-start text-no-wrap">
                <span>{{ $t("TableSum") }}</span>
              </td>
              <template v-for="stock in stockColumns">
                <td :key="'ap_' + stock.stock_id" class="text-center text-no-wrap">
                  <span>{{ $t("Global.money") }}{{ getStockTotal(stock.stock_id, 'average_price') | formatMoney }}</span>
                </td>
                <td :key="'ts_' + stock.stock_id" class="text-center text-no-wrap">
                  <span>{{ getStockTotal(stock.stock_id, 'total_sum') | formatTwoDot }}{{ material ? material.supply_unit : '' }}</span>
                </td>
                <td :key="'tp_' + stock.stock_id" class="text-center text-no-wrap">
                  <span>{{ $t("Global.money") }}{{ getStockTotal(stock.stock_id, 'total_price') | formatMoney }}</span>
                </td>
              </template>
            </tr>
          </template>
          <template v-slot:item="{ item }">
            <tr>
              <td class="text-start text-no-wrap">{{ item.date }}</td>
              <template v-for="stock in stockColumns">
                <td :key="'ap_' + stock.stock_id" class="text-center text-no-wrap">
                  {{ $t("Global.money") }}{{ item[`stock_${stock.stock_id}_average_price`] | formatMoney }}
                </td>
                <td :key="'ts_' + stock.stock_id" class="text-center text-no-wrap">
                  {{ item[`stock_${stock.stock_id}_total_sum`] | formatTwoDot }}{{ material ? material.supply_unit : '' }}
                </td>
                <td :key="'tp_' + stock.stock_id" class="text-center text-no-wrap">
                  {{ $t("Global.money") }}{{ item[`stock_${stock.stock_id}_total_price`] | formatMoney }}
                </td>
              </template>
            </tr>
          </template>
        </v-data-table>
      </div>
    </v-sheet>
  </div>
</template>

<script>
import { mapState } from "vuex";
import i18n from "@/i18n";
import PageAllow from "@/components/PageAllow";
import Breadcrumbs from "@/components/Breadcrumbs";
import MaterialSales from "@/api/material_sales";
import SearcherDatetimeRange from "@/components/plugins/SearcherDatetimeRange";
import SearcherMaterial from "@/components/plugins/SearcherMaterial";
import SearcherShops from "@/components/plugins/SearcherShops";

const defaultDatetimeRage = [
  new Date(new Date().getTime() - 24 * 3600 * 1000).toISOString().substr(0, 10) + " 00:00:00",
  new Date(new Date().getTime() - 24 * 3600 * 1000).toISOString().substr(0, 10) + " 23:59:59",
];

const defaultOptions = {
  distribution_time_from:new Date(new Date().getTime() - 24 * 3600 * 1000).toISOString().substr(0, 10) + " 00:00:00",
  distribution_time_to: new Date(new Date().getTime() - 24 * 3600 * 1000).toISOString().substr(0, 10) + " 23:59:59",
  material_id: null,
  shop_ids: [],
  stock_ids: [],
};

export default {
  components: {
    PageAllow,
    Breadcrumbs,
    SearcherDatetimeRange,
    SearcherMaterial,
    SearcherShops,
  },

  computed: {
    items: () => [
      {
        text: i18n.t("Nav.home"),
        disabled: false,
        href: "/home",
      },
      {
        text: i18n.t("Nav.materialDailySaleCount"),
        disabled: true,
        href: "/material-daily-sale",
      },
    ],
    stockColumns() {
      if (!this.dailySales || !this.dailySales.length) return [];
      const stockIds = [];
      const stockMap = {};
      this.dailySales.forEach((day) => {
        (day.stocks || []).forEach((stock) => {
          if (!stockMap[stock.stock_id]) {
            stockMap[stock.stock_id] = stock;
            stockIds.push(stock.stock_id);
          }
        });
      });
      let result = stockIds.map((id) => stockMap[id]);
      // 当 stocksSelected 为空时，只取前三个仓库
      if (!this.stocksSelected || !this.stocksSelected.length) {
        result = result.slice(0, 3);
      } else {
        // 当 stocksSelected 有值时，只显示 is_stock_select=1 的仓库
        result = result.filter((stock) => stock.is_stock_select == 1);
      }
      return result;
    },
    dailySaleItems() {
      if (!this.stockColumns.length) return [];
      return this.dailySales.map((day) => {
        const row = { date: day.date };
        this.stockColumns.forEach((stockCol) => {
          const stock = (day.stocks || []).find(
            (s) => s.stock_id == stockCol.stock_id
          );
          row[`stock_${stockCol.stock_id}_average_price`] = stock
            ? stock.average_price
            : 0;
          row[`stock_${stockCol.stock_id}_total_sum`] = stock
            ? stock.total_sum
            : 0;
          row[`stock_${stockCol.stock_id}_total_price`] = stock
            ? stock.total_price
            : 0;
        });
        return row;
      });
    },
    headers() {
      const hdrs = [
        {
          text: i18n.t("MaterialDailySale.Fields.date"),
          align: "start",
          sortable: false,
          value: "date",
        },
      ];

      this.stockColumns.forEach((stock) => {
        hdrs.push(
          {
            text: i18n.t("MaterialDailySale.Fields.average_price"),
            value: `stock_${stock.stock_id}_average_price`,
            align: "center",
            sortable: false,
          },
          {
            text: i18n.t("MaterialDailySale.Fields.total_sum"),
            value: `stock_${stock.stock_id}_total_sum`,
            align: "center",
            sortable: false,
          },
          {
            text: i18n.t("MaterialDailySale.Fields.total_price"),
            value: `stock_${stock.stock_id}_total_price`,
            align: "center",
            sortable: false,
          }
        );
      });

      return hdrs;
    },
    subHeaders() {
      const sh = [];
      this.stockColumns.forEach((stock) => {
        sh.push(
          { key: 'ap_' + stock.stock_id, text: this.material.material_name + i18n.t("MaterialDailySale.Fields.average_price") },
          { key: 'ts_' + stock.stock_id, text: this.material.material_name + i18n.t("MaterialDailySale.Fields.total_sum") },
          { key: 'tp_' + stock.stock_id, text: this.material.material_name + i18n.t("MaterialDailySale.Fields.total_price") }
        );
      });
      return sh;
    },
    stocksItems() {
      if (!this.stocks || !this.stocks.length) {
        return [];
      }
      return this.stocks.map((stock) => {
        const Description = stock.code + " / " + stock.name;
        return Object.assign({}, stock, { Description });
      });
    },
    // 使用对象展开运算符将此对象混入到外部对象中
    ...mapState(["loading", "districtID", "settings"]),
  },

  mounted() {
    this.$store.commit(
      "searchDistrict",
      this.settings && this.settings.report_hide_search_district_tree == 1
        ? true
        : false
    );
    this.$commonUtils
      .loadOptions("material-daily-sale", [
        "materialSelected",
        "shopsSelected",
      ])
      .then(
        function (data) {
          if (data.options) {
            this.options = data.options;
            if (
              typeof this.options.distribution_time_from != "undefined" &&
              typeof this.options.distribution_time_to != "undefined"
            ) {
              this.datetimeRage = [
                this.options.distribution_time_from,
                this.options.distribution_time_to,
              ];
            }
          } else this.resetSearch();
          data.selecteds.forEach((selected) => {
            switch (selected.key) {
              case "materialSelected":
                this.materialSelected = selected.value;
                break;
              case "shopsSelected":
                this.shopsSelected = selected.value;
                break;
            }
          });
        }.bind(this)
      );
  },

  data: () => ({
    options: JSON.parse(JSON.stringify(defaultOptions)),
    paging: {},
    datetimeRage: JSON.parse(JSON.stringify(defaultDatetimeRage)),
    datetimeRageChanged: false,
    materialSelected: null,
    shopsSelected: [],
    stocks: [],
    stocksSelected: [],
    stockLoading: false,
    stockSearch: '',
    dailySales: [],
    material: {},
  }),

  methods: {
    resetSearch() {
      this.datetimeRage = JSON.parse(JSON.stringify(defaultDatetimeRage));
      this.options = JSON.parse(JSON.stringify(defaultOptions));
      this.materialSelected = null;
      this.shopsSelected = [];
      this.stocks = [];
      this.material = {};
    },
    onSearch() {
      this.options = Object.assign(this.options, {});
      this.getMaterialDailySaleCountFromAPI();
    },
    getStockTotal(stockId, field) {
      const stock = this.stocks.find((s) => s.id === stockId);
      return stock ? stock[field] : 0;
    },
    getMaterialDailySaleCountFromAPI() {
      var options = JSON.parse(JSON.stringify(this.options));
      if (
        this.settings &&
        this.settings.report_hide_search_district_tree == 1
      ) {
        options = Object.assign(options, {
          district_id: this.districtID,
        });
      }
      this.stockLoading = true;
      MaterialSales.getMaterialDailySaleCount(options).then(
        function (response) {
          if (response.data.status.isOK()) {
            this.dailySales = response.data.daily_sales;
            this.paging = response.data.paging;
            this.stocks = response.data.stocks;
            this.material = response.data.material;
            if (this.dailySales.length == 0) {
              this.options.page = 1;
            }
          } else if (response.data.error.code.isNoResultSet) {
            this.options.page = 1;
          }
          this.stockLoading = false;
        }.bind(this)
      ).catch(
        function () {
          this.stockLoading = false;
        }.bind(this)
      );
    },
    exportExcel() {
      var options = JSON.parse(JSON.stringify(this.options));
      if (
        this.settings &&
        this.settings.report_hide_search_district_tree == 1
      ) {
        options = Object.assign(options, {
          district_id: this.districtID ,
        });
      }
      MaterialSales.materialDailySaleCountExport(options);
    },
  },

  watch: {
    options: {
      deep: true,
      handler() {
        this.getMaterialDailySaleCountFromAPI();
        sessionStorage.setItem(
          "material-daily-sale-options",
          JSON.stringify(this.options)
        );
      },
    },
    datetimeRage: {
      handler() {
        if (this.datetimeRage[0] == null || this.datetimeRage[0] == "") {
          this.options.distribution_time_from =
            new Date(new Date().getTime() - 24 * 3600 * 1000)
              .toISOString()
              .substr(0, 10) + " 00:00:00";
        } else {
          this.options.distribution_time_from = this.datetimeRage[0];
        }

        if (this.datetimeRage[1] == null || this.datetimeRage[1] == "") {
          this.options.distribution_time_to =
            new Date(new Date().getTime() - 24 * 3600 * 1000)
              .toISOString()
              .substr(0, 10) + " 23:59:59";
        } else {
          this.options.distribution_time_to = this.datetimeRage[1];
        }
      },
    },
    // 商品
    materialSelected: {
      handler() {
        this.stocks = [];
        this.stocksSelected = [];
        this.stockLoading = false;
        this.stockSearch = '';
        this.options.material_id = this.materialSelected
          ? this.materialSelected.id
          : null;
        sessionStorage.setItem(
          "material-daily-sale-materialSelected",
          JSON.stringify(this.materialSelected)
        );
      },
    },
    // 门店
    shopsSelected: {
      handler() {
        this.options.shop_ids = this.shopsSelected.map((shop) => shop.id);
        sessionStorage.setItem(
          "material-daily-sale-shopsSelected",
          JSON.stringify(this.shopsSelected)
        );
      },
    },
    // 仓库
    stocksSelected: {
      handler() {
        this.options.stock_ids = this.stocksSelected.map((e) => e.id);
      },
    },
    settings: {
      handler() {
        this.$store.commit(
          "searchDistrict",
          this.settings && this.settings.report_hide_search_district_tree == 1
            ? true
            : false
        );
      },
    },
  },
};
</script>